/* NOVA Pro — extended components */

// Image slider
class ImageSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = this.querySelectorAll('[data-slider-slide]');
    this.index = 0;
    this.total = this.slides.length;
    if (this.total < 2) return;
    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.index + 1));
    if (this.dataset.autoplay === 'true') {
      this._timer = setInterval(() => this.go(this.index + 1), 5000);
    }
  }
  go(i) {
    this.index = (i + this.total) % this.total;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
  }
  disconnectedCallback() { clearInterval(this._timer); }
}
customElements.define('image-slider', ImageSlider);

// Vertical ticker
class VerticalTicker extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-vt-track]');
    if (track) track.innerHTML = track.innerHTML + track.innerHTML;
    const speed = parseInt(this.dataset.speed, 10) || 16;
    this.style.setProperty('--vt-speed', `${speed}s`);
  }
}
customElements.define('vertical-ticker', VerticalTicker);

// Comparison slider
class ComparisonSlider extends HTMLElement {
  connectedCallback() {
    this.after = this.querySelector('[data-cs-after]');
    this.handle = this.querySelector('[data-cs-handle]');
    let dragging = false;
    const set = (pct) => {
      const p = Math.max(5, Math.min(95, pct));
      this.after.style.width = `${p}%`;
      this.handle.style.left = `${p}%`;
    };
    const move = (clientX) => {
      const rect = this.getBoundingClientRect();
      set(((clientX - rect.left) / rect.width) * 100);
    };
    this.handle?.addEventListener('mousedown', () => { dragging = true; });
    window.addEventListener('mouseup', () => { dragging = false; });
    window.addEventListener('mousemove', (e) => { if (dragging) move(e.clientX); });
    this.addEventListener('touchmove', (e) => move(e.touches[0].clientX), { passive: true });
  }
}
customElements.define('comparison-slider', ComparisonSlider);

// Shoppable image pins
class ShoppableImage extends HTMLElement {
  connectedCallback() {
    this.cards = this.querySelectorAll('[data-pin-card]');
    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => e.stopPropagation());
    });
    this.querySelectorAll('[data-pin]').forEach((pin) => {
      pin.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = pin.dataset.pin;
        this.cards.forEach((c) => { c.hidden = c.dataset.pinCard !== id; });
      });
    });
    this._onDocClick = () => {
      this.cards.forEach((c) => { c.hidden = true; });
    };
    document.addEventListener('click', this._onDocClick);
  }
  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
  }
}
customElements.define('shoppable-image', ShoppableImage);

// Bundle deals selector
class BundleDeals extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-bundle-deal]').forEach((deal) => {
      deal.addEventListener('click', () => {
        this.querySelectorAll('[data-bundle-deal]').forEach((d) => d.dataset.selected = 'false');
        deal.dataset.selected = 'true';
        const qty = deal.dataset.qty;
        const input = document.querySelector('product-form form input[name="quantity"]')
          || document.querySelector('#quantity');
        if (input && qty) {
          input.value = qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    // Auto-priced deals track the live variant price
    this._onVariant = (e) => this.reprice(e.detail?.variant);
    document.addEventListener('variant:change', this._onVariant);
  }
  disconnectedCallback() { document.removeEventListener('variant:change', this._onVariant); }
  reprice(variant) {
    if (!variant || typeof formatMoney !== 'function') return;
    const compareUnit = Math.max(variant.compare_at_price || 0, variant.price);
    this.querySelectorAll('[data-bundle-deal][data-auto-price]').forEach((deal) => {
      const qty = parseInt(deal.dataset.qty, 10) || 1;
      const pct = parseInt(deal.dataset.pct, 10) || 0;
      const total = variant.price * qty;
      const price = pct > 0 ? Math.round(total * (100 - pct) / 100) : total;
      const compare = Math.max(compareUnit * qty, price);
      const priceEl = deal.querySelector('[data-deal-price]');
      if (priceEl) priceEl.textContent = formatMoney(price);
      const compareEl = deal.querySelector('[data-deal-compare]');
      if (compareEl) {
        compareEl.hidden = !(compare > price);
        if (compare > price) compareEl.textContent = formatMoney(compare);
      }
    });
  }
}
customElements.define('bundle-deals', BundleDeals);

// Scroll progress
class ScrollProgress extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-scroll-bar]');
    this._onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? (window.scrollY / h) * 100 : 0;
      if (this.bar) this.bar.style.width = `${pct}%`;
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this._onScroll); }
}
customElements.define('scroll-progress', ScrollProgress);

// Music player toggle
class MusicPlayer extends HTMLElement {
  connectedCallback() {
    this.audio = this.querySelector('audio');
    this.btn = this.querySelector('[data-music-toggle]');
    this.btn?.addEventListener('click', () => {
      if (!this.audio) return;
      if (this.audio.paused) { this.audio.play(); this.btn.textContent = '⏸'; }
      else { this.audio.pause(); this.btn.textContent = '♪'; }
    });
  }
}
customElements.define('music-player', MusicPlayer);

// Parallax hero
class ParallaxHero extends HTMLElement {
  connectedCallback() {
    this.bg = this.querySelector('[data-parallax-bg]');
    if (!this.bg) return;
    this._onScroll = () => {
      const rect = this.getBoundingClientRect();
      const offset = Math.max(0, -rect.top * 0.3);
      this.bg.style.transform = `translateY(${offset}px)`;
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }
  disconnectedCallback() { window.removeEventListener('scroll', this._onScroll); }
}
customElements.define('parallax-hero', ParallaxHero);

// Predictive search
class PredictiveSearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;
    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.hidden = true; return; }
      timer = setTimeout(() => this.search(q), 250);
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.results.hidden = true;
    });
  }
  async search(q) {
    const res = await fetch(`${window.Shopify?.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const html = doc.querySelector('[data-predictive-results]')?.innerHTML;
    if (html) { this.results.innerHTML = html; this.results.hidden = false; }
  }
}
customElements.define('predictive-search-form', PredictiveSearchForm);

// Mobile drawer
class MobileDrawer extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    document.querySelectorAll('[data-drawer-open]').forEach((btn) => btn.addEventListener('click', () => this.open()));
  }
  open() { this.dataset.open = 'true'; document.body.style.overflow = 'hidden'; }
  close() { this.dataset.open = 'false'; document.body.style.overflow = ''; }
}
customElements.define('mobile-drawer', MobileDrawer);

// Cart upsell add
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-upsell-add]');
  if (!btn) return;
  e.preventDefault();
  const id = btn.dataset.variantId;
  if (!id) return;
  const addResponse = await fetch(novaRoute('cartAdd', '/cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, quantity: 1 }) });
  if (!addResponse.ok) return;
  const cart = await (await fetch(novaRoute('cart', '/cart') + '.js')).json();
  if (typeof updateCartCount === 'function') updateCartCount(cart.item_count);
  await window.NovaCart?.checkGift?.(cart);
  const drawer = document.querySelector('cart-drawer');
  if (drawer?.refresh) { await drawer.refresh(); drawer.open(); }
});

// Track order (redirect to carrier lookup placeholder)
document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-track-order]');
  if (!form) return;
  e.preventDefault();
  const num = form.querySelector('[name="order_number"]')?.value;
  const email = form.querySelector('[name="email"]')?.value;
  if (num && email) window.location.href = `/account/login?email=${encodeURIComponent(email)}`;
});
// ---------- Shoppable video reels (mobile-only, TikTok-style vertical feed) ----------
class VideoReels extends HTMLElement {
  connectedCallback() {
    this.feed = this.querySelector('[data-reels-feed]');
    this.tryonFeed = this.querySelector('[data-tryon-feed]');
    this.scroller = this.querySelector('[data-reels-scroller]');
    this.items = Array.from(this.querySelectorAll('[data-reels-item]'));
    this.soundOn = false;
    this.setupSocial();
    this._onTabClick = (event) => {
      const tab = event.target.closest('[data-reels-tab]');
      if (!tab || (!this.contains(tab) && !this.feed?.contains(tab))) return;
      if (tab.dataset.reelsTab === 'reels') this.openReels();
      else if (tab.dataset.reelsTab === 'tryon') this.openTryOn();
      else this.close();
    };
    this.addEventListener('click', this._onTabClick);
    if (this.feed) {
      this._feedAnchor = document.createComment('reels-feed');
      this.feed.before(this._feedAnchor);
      document.body.append(this.feed);
      this.feed.addEventListener('click', this._onTabClick);
    }

    this._mediaAbort = new AbortController();
    this.items.forEach(item => this.bindMedia(item));
    if (this.feed && this.scroller && this.items.length) this._observer = new IntersectionObserver(entries => {
      if (this.feed.hidden || document.hidden) return;
      const visible = entries.filter(entry => entry.isIntersecting && entry.intersectionRatio >= .6).sort((a,b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) this.activateItem(visible.target);
    }, { root: this.scroller, threshold: .6 });
    this._onVisibility = () => {
      clearTimeout(this._prefetchTimer);
      if (document.hidden) {
        this.feed.dataset.suspended = 'true';
        this.items.forEach(item => this.releaseMedia(item));
        clearInterval(this._danmakuTimer);
      } else if (this.feed && !this.feed.hidden && this._activeItem) {
        this.feed.dataset.suspended = 'false';
        this.activateDanmaku(this._activeItem);
        this.playItem(this._activeItem);
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    this._onFeedClick = (e) => {
      const action = e.target.closest('[data-reels-like],[data-reels-save],[data-reels-comment]');
      if (action) { this.socialAction(action); return; }
      const retry = e.target.closest('[data-reels-retry]');
      if (retry) { const item = retry.closest('[data-reels-item]'); this.releaseMedia(item); item._manualPaused = false; this.attachMedia(item); this.playItem(item); return; }
      const mute = e.target.closest('[data-reels-mute]');
      if (mute) {
        this.soundOn = !this.soundOn;
        this.applySound();
        return;
      }
      const video = e.target.closest('video');
      if (video) {
        const item = video.closest('[data-reels-item]');
        item._manualPaused = !video.paused;
        item.dataset.videoPaused = String(item._manualPaused);
        if (item._manualPaused) video.pause(); else this.playItem(item);
      }
    };
    this.feed?.addEventListener('click', this._onFeedClick);

    // Browser/phone back gesture closes the feed
    this._onPop = () => {
      if ((this.feed && !this.feed.hidden) || (this.tryonFeed && !this.tryonFeed.hidden)) this.close(false);
    };
    window.addEventListener('popstate', this._onPop);
    if (window.location.hash === '#reels' && this.feed) this.openReels(false);
    if (window.location.hash === '#try-on' && this.tryonFeed) this.openTryOn(false);
  }
  disconnectedCallback() {
    clearInterval(this._danmakuTimer);
    clearTimeout(this._prefetchTimer);
    this._mediaAbort?.abort();
    document.removeEventListener('visibilitychange', this._onVisibility);
    this.items.forEach(item => this.releaseMedia(item));
    this.comments?.close();
    this._observer?.disconnect();
    this.removeEventListener('click', this._onTabClick);
    this.feed?.removeEventListener('click', this._onFeedClick);
    this.feed?.removeEventListener('click', this._onTabClick);
    if (this._feedAnchor?.parentNode && this.feed) this._feedAnchor.replaceWith(this.feed);
    if (document.body.classList.contains('reels-open')) this.close(false);
    window.removeEventListener('popstate', this._onPop);
  }

  setActiveTabs(mode) {
    const tabs = new Set([...this.querySelectorAll('[data-reels-tab]'), ...(this.feed?.querySelectorAll('[data-reels-tab]') || [])]);
    tabs.forEach((tab) => {
      const active = tab.dataset.reelsTab === mode;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active);
    });
  }
  setMuteUI(item, muted) {
    const btn = item.querySelector('[data-reels-mute]');
    if (btn) btn.dataset.muted = String(muted);
  }
  applySound() {
    this.items.forEach((item) => {
      const video = item.querySelector('video');
      if (video) video.muted = !this.soundOn;
      this.setMuteUI(item, !this.soundOn);
    });
  }

  constrainedNetwork() {
    const connection = navigator.connection;
    return !!connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '');
  }
  hydrateCover(item) {
    const cover = item?.querySelector('[data-reels-cover]');
    if (cover && !cover.hasAttribute('src') && cover.dataset.src) cover.src = cover.dataset.src;
  }
  bindMedia(item) {
    const video = item.querySelector('video');
    if (!video) return;
    const options = { signal: this._mediaAbort.signal };
    video.addEventListener('playing', () => {
      if (this.feed.hidden || document.hidden || item !== this._activeItem || this.comments?.open || item._manualPaused) { video.pause(); return; }
      item.dataset.videoReady = 'true';
      item.dataset.videoPaused = 'false';
      item.querySelector('[data-reels-retry]').hidden = true;
      this.scheduleNext(item);
    }, options);
    video.addEventListener('canplaythrough', () => { if (item === this._activeItem) this.scheduleNext(item); }, options);
    video.addEventListener('loadedmetadata', () => {
      if (item._resumeTime > 0 && Number.isFinite(video.duration) && item._resumeTime < video.duration) video.currentTime = item._resumeTime;
      item._resumeTime = 0;
    }, options);
    video.addEventListener('error', () => {
      if (!video.hasAttribute('src')) return;
      if (item._fallbackSrc && video.getAttribute('src') !== item._fallbackSrc) {
        item._playToken = (item._playToken || 0) + 1;
        video.src = item._fallbackSrc;
        video.load();
        if (item === this._activeItem) this.playItem(item);
        return;
      }
      item.dataset.videoReady = 'false';
      item.querySelector('[data-reels-retry]').hidden = false;
    }, options);
  }
  attachMedia(item, preload = 'auto') {
    this.hydrateCover(item);
    const video = item?.querySelector('video');
    if (!video) return;
    video.preload = preload;
    if (video.hasAttribute('src')) return;
    const sources = Array.from(item.querySelector('[data-reels-sources]')?.content.querySelectorAll('source') || []);
    const mp4 = sources.filter(source => source.type === 'video/mp4').sort((a,b) => Number(a.dataset.height) - Number(b.dataset.height));
    const target = this.constrainedNetwork() || matchMedia('(max-width: 749px)').matches ? 720 : 1080;
    const best = mp4.filter(source => Number(source.dataset.height) <= target).pop() || mp4[0];
    const hls = sources.find(source => /mpegurl/i.test(source.type));
    const chosen = hls && video.canPlayType('application/vnd.apple.mpegurl') ? hls : best;
    if (!chosen?.dataset.src) { item.querySelector('[data-reels-retry]').hidden = false; return; }
    item._fallbackSrc = best?.dataset.src;
    item.dataset.videoReady = 'false';
    item.querySelector('[data-reels-retry]').hidden = true;
    video.src = chosen.dataset.src;
    video.load();
  }
  playItem(item) {
    const video = item?.querySelector('video');
    if (!video || this.feed.hidden || document.hidden || item !== this._activeItem || item._manualPaused || this.comments?.open) return;
    this.attachMedia(item);
    if (!video.hasAttribute('src')) return;
    const token = item._playToken = (item._playToken || 0) + 1;
    video.muted = !this.soundOn;
    video.play().catch(error => {
      if (token !== item._playToken || this.feed.hidden || document.hidden || item !== this._activeItem || item._manualPaused || this.comments?.open) return;
      if (error.name === 'NotAllowedError' && !video.muted) {
        video.muted = true;
        this.setMuteUI(item, true);
        video.play().catch(() => {
          if (token === item._playToken && item === this._activeItem && !this.feed.hidden && !document.hidden) item.querySelector('[data-reels-retry]').hidden = false;
        });
      } else if (error.name !== 'AbortError') item.querySelector('[data-reels-retry]').hidden = false;
    });
  }
  activateItem(item) {
    if (!item || this.feed.hidden || document.hidden || item === this._activeItem) return;
    clearTimeout(this._prefetchTimer);
    const index = this.items.indexOf(item);
    this.items.forEach((other, otherIndex) => {
      other.dataset.reelActive = String(other === item);
      if (other === item) return;
      const video = other.querySelector('video');
      other._playToken = (other._playToken || 0) + 1;
      video?.pause();
      if (Math.abs(otherIndex - index) > 1) this.releaseMedia(other);
    });
    this._activeItem = item;
    this.hydrateCover(item);
    this.activateDanmaku(item);
    this.playItem(item);
    if (!item.querySelector('video')) this.scheduleNext(item);
  }
  scheduleNext(item) {
    clearTimeout(this._prefetchTimer);
    if (this.constrainedNetwork()) return;
    const next = this.items[this.items.indexOf(item) + 1];
    if (!next) return;
    this._prefetchTimer = setTimeout(() => {
      const video = item.querySelector('video');
      if (this.constrainedNetwork() || item !== this._activeItem || this.feed.hidden || document.hidden || this.comments?.open || item._manualPaused || (video && video.paused)) return;
      if (video) {
        const buffered = video.buffered.length ? video.buffered.end(video.buffered.length - 1) - video.currentTime : 0;
        if (video.readyState < 3 || buffered < Math.min(2, Math.max(0, video.duration - video.currentTime))) return;
      }
      this.attachMedia(next, 'metadata');
    }, 1000);
  }
  releaseMedia(item) {
    const video = item?.querySelector('video');
    if (!video || !video.hasAttribute('src')) return;
    item._playToken = (item._playToken || 0) + 1;
    if (video.currentTime > 0) item._resumeTime = video.currentTime;
    video.pause();
    video.removeAttribute('src');
    video.preload = 'none';
    video.load();
    item.dataset.videoReady = 'false';
  }
  setupSocial() {
    this.comments = this.feed?.querySelector('[data-reels-comments]');
    this.items.forEach(item => {
      let state = {};
      try { state = JSON.parse(localStorage.getItem('nova-reel:' + item.dataset.reelId) || '{}') || {}; } catch {}
      item.reelState = { like: state.like === true, save: state.save === true, comments: Array.isArray(state.comments) ? state.comments.filter(x => typeof x === 'string').map(x => x.slice(0,120)).slice(-100) : [] };
      ['like','save'].forEach(key => item.querySelector('[data-reels-' + key + ']')?.setAttribute('aria-pressed', String(item.reelState[key])));
    });
    this.comments?.querySelector('[data-reels-dismiss]').addEventListener('click', () => this.comments.close());
    this.comments?.addEventListener('click', event => { if (event.target === this.comments && event.clientY < this.comments.getBoundingClientRect().top) this.comments.close(); });
    this.comments?.addEventListener('close', () => {
      if (this.feed && !this.feed.hidden) {
        if (this._commentWasPlaying) this.playItem(this._commentItem);
        if (this._commentItem) this.activateDanmaku(this._commentItem);
      }
    });
    this.comments?.querySelector('form').addEventListener('submit', event => {
      event.preventDefault();
      const input = event.target.elements.comment;
      const text = input.value.trim().slice(0,120);
      if (!text || !this._commentItem) return;
      if (!this._commentItem.reelState.comments.includes(text)) this._commentItem.reelState.comments.push(text);
      this._commentItem.reelState.comments = this._commentItem.reelState.comments.slice(-100);
      this.storeSocial(this._commentItem);
      this.renderComments(); input.value = '';

    });
  }
  storeSocial(item) {
    try { localStorage.setItem('nova-reel:' + item.dataset.reelId, JSON.stringify(item.reelState)); }
    catch { const status = this.comments?.querySelector('[role=status]'); if (status) status.textContent = this.feed.dataset.storageError; }
  }
  socialAction(button) {
    const item = button.closest('[data-reels-item]');
    if (!item) return;
    if (button.hasAttribute('data-reels-comment')) {
      this._commentItem = item;
      const video = item.querySelector('video');
      this._commentWasPlaying = video && !video.paused;
      video?.pause();
      clearTimeout(this._prefetchTimer);
      this.renderComments();
      this.comments.showModal();
      return;
    }
    const key = button.hasAttribute('data-reels-like') ? 'like' : 'save';
    item.reelState[key] = !item.reelState[key];
    button.setAttribute('aria-pressed', String(item.reelState[key]));
    this.animateSocial(button, key, item.reelState[key]);
    this.storeSocial(item);
  }
  animateSocial(button, key, selected) {
    const icon = button.querySelector('svg');
    if (!icon || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    icon.getAnimations().forEach(animation => animation.cancel());
    button.querySelectorAll('.reels__burst').forEach(particle => particle.remove());
    icon.animate(selected ? [
      { transform: 'scale(1) rotate(0deg)' },
      { transform: 'scale(.72) rotate(-12deg)', offset: .18 },
      { transform: 'scale(1.4) rotate(8deg)', offset: .46 },
      { transform: 'scale(.94) rotate(-4deg)', offset: .72 },
      { transform: 'scale(1.08) rotate(0deg)' }
    ] : [
      { transform: 'scale(1.08)' }, { transform: 'scale(.8)', offset: .4 }, { transform: 'scale(1)' }
    ], { duration: selected ? 560 : 230, easing: 'cubic-bezier(.22,.68,.32,1)' });
    if (!selected) return;
    for (let i = 0; i < 7; i++) {
      const particle = document.createElement('i');
      particle.className = 'reels__burst';
      particle.setAttribute('aria-hidden', 'true');
      particle.textContent = key === 'like' ? '♥' : '✦';
      particle.style.color = key === 'like' ? '#fe2c55' : '#ffd45a';
      button.append(particle);
      const angle = (i / 7) * Math.PI * 2 - Math.PI / 2;
      const distance = 28 + (i % 3) * 9;
      particle.animate([
        { transform: 'translate(-50%,-50%) scale(.25)', opacity: 0 },
        { opacity: 1, offset: .2 },
        { transform: `translate(calc(-50% + ${Math.cos(angle) * distance}px),calc(-50% + ${Math.sin(angle) * distance}px)) rotate(${i * 35}deg) scale(.55)`, opacity: 0 }
      ], { duration: 620, delay: i * 12, easing: 'cubic-bezier(.12,.65,.35,1)', fill: 'both' }).finished.catch(() => {}).finally(() => particle.remove());
    }
  }
  renderComments() {
    const list = this.comments.querySelector('[data-reels-comment-list]');
    list.replaceChildren();
    const texts = this._commentItem.reelState.comments;
    (texts.length ? texts : [this.feed.dataset.emptyComments]).forEach(text => {
      const p = document.createElement('p'); p.textContent = text; list.append(p);
    });
    list.scrollTop = list.scrollHeight;
  }
  emitDanmaku(item, text) {
    const layer = item.querySelector('[data-reels-danmaku]');
    if (!layer) return;
    item.reelSeen ||= new Set();
    const normalized = text.trim().normalize('NFC');
    if (!normalized || item.reelSeen.has(normalized)) return;
    item.reelSeen.add(normalized);
    const span = document.createElement('span'); span.textContent = normalized;
    span.style.top = ((this._danmakuLane++ % 5) * 18) + '%';
    layer.append(span);
    span.addEventListener('animationend', () => span.remove(), { once:true });
    setTimeout(() => span.remove(), 9500);
    while (layer.children.length > 8) layer.firstElementChild.remove();
  }
  activateDanmaku(item) {
    clearInterval(this._danmakuTimer);
    this.items.forEach(x => x.querySelector('[data-reels-danmaku]')?.replaceChildren());
    this._danmakuLane = 0;
    let seeds = '';
    try { seeds = item.querySelector('[data-reels-seeds]')?.textContent || ''; } catch {}
    item.reelSeen ||= new Set();
    const texts = [...new Set([...String(seeds).split(/\r?\n/), ...item.reelState.comments].map(x => x.trim().normalize('NFC')).filter(Boolean))].filter(text => !item.reelSeen.has(text)).slice(-150);
    if (!texts.length) return;
    let index = 0;
    const emit = () => {
      if (this.feed.hidden || document.hidden || this.comments.open || item.querySelector('video')?.paused) return;
      if (index < texts.length) this.emitDanmaku(item, texts[index++]);
      if (index >= texts.length) { clearInterval(this._danmakuTimer); this._danmakuTimer = null; }
    };
    this._danmakuTimer = setInterval(emit, 2200); emit();
  }
  pauseReels() {
    clearInterval(this._danmakuTimer);
    clearTimeout(this._prefetchTimer);
    this.items.forEach(item => { this._observer?.unobserve(item); this.releaseMedia(item); });
    this._activeItem = null;
  }
  openReels(pushHash = true) {
    if (!this.feed || !this.feed.hidden) return;
    if (this.tryonFeed) this.tryonFeed.hidden = true;
    if (!document.body.classList.contains('reels-open') && !document.body.classList.contains('tryon-open')) this._previousOverflow = document.body.style.overflow;
    this.feed.hidden = false;
    document.body.classList.remove('tryon-open');
    document.body.classList.add('reels-open');
    document.body.style.overflow = 'hidden';
    this.setActiveTabs('reels');
    // Opened by a tap = user gesture, so sound is allowed
    this.soundOn = true;
    this.applySound();
    this.items.forEach(item => { item.reelSeen = new Set(); });
    if (this.scroller) this.scroller.scrollTop = 0;
    this.items.forEach((item) => this._observer?.observe(item));
    if (this.items[0]) this.activateItem(this.items[0]);
    if (pushHash && window.location.hash !== '#reels') {
      history.pushState({ reels: true }, '', '#reels');
    }
  }
  open(pushHash = true) { this.openReels(pushHash); }
  openTryOn(pushHash = true) {
    if (!this.tryonFeed) return;
    if (this.feed) this.feed.hidden = true;
    this.pauseReels();
    this.comments?.close();
    this.tryonFeed.hidden = false;
    document.body.classList.remove('reels-open');
    document.body.classList.add('tryon-open');
    document.body.style.overflow = 'hidden';
    this.setActiveTabs('tryon');
    if (pushHash && window.location.hash !== '#try-on') history.pushState({ tryon: true }, '', '#try-on');
  }
  close(updateHistory = true) {
    if (this.feed) this.feed.hidden = true;
    this.pauseReels();
    this.comments?.close();
    if (this.tryonFeed) this.tryonFeed.hidden = true;
    document.body.classList.remove('reels-open', 'tryon-open');
    document.body.style.overflow = this._previousOverflow || '';
    this.setActiveTabs('home');
    if (updateHistory && (window.location.hash === '#reels' || window.location.hash === '#try-on')) {
      history.replaceState({}, '', window.location.pathname + window.location.search);
    }
  }
}
customElements.define('video-reels', VideoReels);

class NovaVirtualTryOn extends HTMLElement {
  connectedCallback() {
    try {
      this.config = JSON.parse(this.querySelector('[data-tryon-config]')?.textContent || '{}');
    } catch {
      this.config = {};
    }
    this.endpoint = String(this.config.endpoint || '').replace(/\/+$/, '');
    this.products = new Map((this.config.products || []).map((product) => [product.handle, product]));
    this.maxLayers = Math.min(4, Math.max(1, Number(this.config.maxLayers) || 3));
    this.selected = [];
    this.input = this.querySelector('[data-tryon-input]');
    this.consent = this.querySelector('[data-tryon-consent]');
    this.createButton = this.querySelector('[data-tryon-create]');
    this.onboard = this.querySelector('[data-tryon-onboard]');
    this.result = this.querySelector('[data-tryon-result]');
    this.loading = this.querySelector('[data-tryon-loading]');
    this.message = this.querySelector('[data-tryon-message]');
    this.look = this.querySelector('[data-tryon-look]');
    this.chips = this.querySelector('[data-tryon-chips]');
    this.resetButton = this.querySelector('[data-tryon-reset]');
    this.ready = false;
    this.input?.addEventListener('change', () => this.readPhoto());
    this.consent?.addEventListener('change', () => this.updateCreateButton());
    this.createButton?.addEventListener('click', () => this.createModel());
    this.resetButton?.addEventListener('click', () => this.reset());
    this.querySelector('[data-tryon-products]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tryon-product]');
      if (button) this.toggleProduct(button.dataset.tryonProduct);
    });
    this.checkStatus();
  }

  async api(path, payload, method = 'POST') {
    if (!this.endpoint || !this.config.token) throw new Error('The virtual fitting room is not connected yet.');
    const response = await fetch(`${this.endpoint}${path}`, {
      method,
      headers: { 'content-type': 'application/json', 'x-nova-token': this.config.token },
      body: method === 'GET' ? undefined : JSON.stringify(payload || {})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'The fitting room could not connect. Please try again.');
    return data;
  }

  async checkStatus() {
    try {
      const status = await this.api('/api/tryon/status', null, 'GET');
      this.ready = !!status.ready;
      if (!this.ready) this.showMessage('2D Try On is being configured. Please check back shortly.', true);
    } catch (error) {
      this.ready = false;
      this.showMessage(error.message, true);
    }
    this.updateCreateButton();
  }

  async optimizePhoto(file) {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error('Please choose a JPEG, PNG, or WebP photo.');
    if (file.size > 12 * 1024 * 1024) throw new Error('Please choose a photo under 12 MB.');
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d', { alpha: false });
    context.fillStyle = '#f2efec';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', 0.84);
  }

  async readPhoto() {
    const file = this.input?.files?.[0];
    this.sourceImage = '';
    try {
      if (file) {
        this.sourceImage = await this.optimizePhoto(file);
        const label = this.querySelector('.tryon__upload span');
        if (label) label.textContent = file.name.length > 25 ? `${file.name.slice(0, 22)}…` : file.name;
        this.showMessage('Photo ready. Confirm permission, then create your model.');
      }
    } catch (error) {
      this.input.value = '';
      this.showMessage(error.message, true);
    }
    this.updateCreateButton();
  }

  updateCreateButton() {
    if (this.createButton) this.createButton.disabled = !this.ready || !this.sourceImage || !this.consent?.checked || this.busy;
  }

  setBusy(active, text = 'Styling your look…') {
    this.busy = active;
    if (this.loading) {
      this.loading.hidden = !active;
      const strong = this.loading.querySelector('strong');
      if (strong) strong.textContent = text;
    }
    this.querySelectorAll('[data-tryon-product]').forEach((button) => { button.disabled = active; });
    this.updateCreateButton();
  }

  showMessage(text, error = false) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.classList.toggle('is-error', error);
    this.message.classList.toggle('is-show', !!text);
  }

  async createModel() {
    if (this.busy || !this.sourceImage || !this.consent?.checked) return;
    this.setBusy(true, 'Creating your private 2D model…');
    this.showMessage('');
    try {
      const response = await this.api('/api/tryon/prepare', { person_image: this.sourceImage, consent: true, adult_confirmed: true });
      this.baseModel = response.data.image;
      this.currentImage = this.baseModel;
      this.result.src = this.currentImage;
      this.result.hidden = false;
      this.onboard.hidden = true;
      this.resetButton.hidden = false;
      this.selected = [];
      this.updateSelectionUI();
      this.showMessage('Your model is ready. Tap pieces on the rail to build a look.');
      this.record('tryon_model_created');
    } catch (error) {
      this.showMessage(error.message, true);
    } finally {
      this.setBusy(false);
    }
  }

  layerRank(product) {
    const text = `${product.type || ''} ${product.tags || ''} ${product.title || ''}`.toLowerCase();
    if (/coat|jacket|blazer|outerwear|trench/.test(text)) return 50;
    if (/cardigan|sweater|hoodie|vest/.test(text)) return 40;
    if (/dress|jumpsuit|romper|overall/.test(text)) return 30;
    if (/pants|trouser|jean|skirt|short/.test(text)) return 25;
    if (/shirt|blouse|top|tee|tank|bra|base/.test(text)) return 10;
    return 35;
  }

  async toggleProduct(handle) {
    if (this.busy) return;
    if (!this.baseModel) return this.showMessage('Create your 2D model first, then choose pieces.', true);
    const product = this.products.get(handle);
    if (!product) return;
    const index = this.selected.findIndex((item) => item.handle === handle);
    if (index >= 0) this.selected.splice(index, 1);
    else {
      if (this.selected.length >= this.maxLayers) return this.showMessage(`This fitting room supports up to ${this.maxLayers} pieces per look. Remove one first.`, true);
      this.selected.push(product);
    }
    this.selected.sort((a, b) => this.layerRank(a) - this.layerRank(b));
    this.updateSelectionUI();
    if (!this.selected.length) {
      this.currentImage = this.baseModel;
      this.result.src = this.baseModel;
      return this.showMessage('Base model restored. Choose a new piece when you’re ready.');
    }
    await this.renderLook();
  }

  updateSelectionUI() {
    this.querySelectorAll('[data-tryon-product]').forEach((button) => {
      const active = this.selected.some((item) => item.handle === button.dataset.tryonProduct);
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', String(active));
      const mark = button.querySelector('.tryon__product-image i');
      if (mark) mark.textContent = active ? '✓' : '+';
    });
    if (this.look) this.look.hidden = !this.selected.length;
    if (this.chips) {
      this.chips.replaceChildren(...this.selected.map((product, index) => {
        const chip = document.createElement('span');
        chip.textContent = `${index + 1}. ${product.title}`;
        return chip;
      }));
    }
  }

  async renderLook() {
    this.setBusy(true, this.selected.length > 1 ? `Layering ${this.selected.length} pieces…` : 'Trying on your selection…');
    this.showMessage('');
    try {
      const garments = this.selected.map((product, index) => ({
        handle: product.handle,
        title: product.title,
        type: product.type,
        image_url: new URL(product.image, window.location.href).href,
        layer: index * 10
      }));
      const response = await this.api('/api/tryon/render', { base_model_image: this.baseModel, garments, consent: true, adult_confirmed: true });
      this.currentImage = response.data.image;
      this.result.src = this.currentImage;
      this.showMessage(`${this.selected.length} ${this.selected.length === 1 ? 'piece' : 'pieces'} styled. Tap another item to layer it.`);
      this.record('tryon_look_created');
    } catch (error) {
      this.showMessage(error.message, true);
    } finally {
      this.setBusy(false);
    }
  }

  record(event) {
    this.api('/api/events', { event, page: 'tryon' }).catch(() => {});
  }

  reset() {
    this.sourceImage = '';
    this.baseModel = '';
    this.currentImage = '';
    this.selected = [];
    if (this.input) this.input.value = '';
    if (this.consent) this.consent.checked = false;
    if (this.result) { this.result.hidden = true; this.result.removeAttribute('src'); }
    if (this.onboard) this.onboard.hidden = false;
    if (this.resetButton) this.resetButton.hidden = true;
    const label = this.querySelector('.tryon__upload span');
    if (label) label.textContent = 'Choose full-body photo';
    this.updateSelectionUI();
    this.showMessage('');
    this.updateCreateButton();
  }
}
customElements.define('nova-virtual-tryon', NovaVirtualTryOn);

document.addEventListener('click', (event) => {
  const unitButton = event.target.closest('[data-size-unit]');
  if (!unitButton) return;
  const popup = unitButton.closest('.size-guide-popup');
  if (!popup) return;
  const unit = unitButton.dataset.sizeUnit;
  popup.querySelectorAll('[data-size-unit]').forEach((button) => {
    button.dataset.active = String(button === unitButton);
  });
  popup.querySelectorAll('[data-size-value]').forEach((value) => {
    value.hidden = value.dataset.sizeValue !== unit;
  });
});
